import React, { useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

interface HotWork {
  id: number;
  refNo: string;
  createdDate: string;
  workBy: string;
  location: string;
  status: string;
  statusColor: string;
  jobNumber: string;
  natureOfJob: string;
  personName: string;
}

interface Props {
  data: HotWork[];
  onEdit: (item: HotWork) => void;
  onDelete: (item: HotWork) => void;
  isLoading?: boolean;
  readOnly?: boolean;
}

const HotWorkAssessmentTable: React.FC<Props> = ({ data, onEdit, onDelete, isLoading = false, readOnly = false }) => {
  const navigate = useNavigate();

  const getDotColor = (status: string) => {
    if (status === 'Assessment Pending') return 'bg-orange-500';
    if (status === 'Clearance Done (AO)') return 'bg-green-600';
    return 'bg-gray-400';
  };

  const location = useLocation();

  useEffect(() => {
    if (!readOnly && location.state?.openEdit) {
      onEdit(location.state.editItem);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-full text-sm text-left text-gray-700">
          <thead>
            <tr className="h-[45px]">
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Sl. No</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Ref No.</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Created Date</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Work By</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Location</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Status</th>
              {!readOnly && <th className="px-4 py-1 border text-xs font-medium text-gray-500">Actions</th>}
            </tr>
          </thead>

          <tbody>
            {data.map((item, index) => (
              <tr key={item.id} onClick={() => navigate(`/HotWorkApproval/${item.id}`)} className="border hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-3 border font-medium">{index + 1}</td>
                <td className="px-4 py-3 border font-medium">{item.refNo}</td>
                <td className="px-4 py-3 border font-medium">{item.createdDate}</td>
                <td className="px-4 py-3 border font-medium">{item.workBy}</td>
                <td className="px-4 py-3 border font-medium">{item.location}</td>

                <td className={`px-4 py-3 font-medium border ${item.statusColor}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${getDotColor(item.status)}`} />
                    {item.status}
                  </div>
                </td>

                {!readOnly && (
                  <td className="px-4 py-3 border" onClick={(e) => e.stopPropagation()}>
                    {item.status?.trim().toLowerCase() === 'assessment pending' && (
                      <div className="flex gap-3">
                        <button className="text-red-500" onClick={() => onDelete(item)}>
                          <Trash2 size={18} />
                        </button>

                        <button className="text-blue-500" onClick={() => onEdit(item)}>
                          <Pencil size={18} />
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HotWorkAssessmentTable;
